import { axiosInstance } from 'src/constants/axiosInstance';
import { ApiResponse } from 'src/types/commonResponseTypes';
import { MEMORY_BATCH_RESYNC_FREQUENCY } from 'src/constants/memory';
import {
  SourceOperationAttempt,
  SourceCatalogMetadata,
  SourceCatalogPage,
  SourceCatalogRunResult,
  SourceCatalogSort,
  SourceCatalogSummary,
  SourceContentPage,
  SourceContentRetryResult,
  SourceFolderSummary,
  SourceOperationsCapability,
  SourceOperationsDetail,
  SourceOperationsPage,
  SourceReconnectAction,
  SourceRole,
  SourceScheduleSummary,
  SourceSummary,
  SourceVisibility,
  SourceProvider,
  WorkspaceOperationalFailure,
} from 'src/types/sourceOperations';
import { handleError } from 'src/utils/commonErrorUtils';

const mutationHeaders = (idempotencyKey: string) => ({
  'Idempotency-Key': idempotencyKey,
});

export const getSourceOperationsCapability = async (): Promise<
  ApiResponse<SourceOperationsCapability>
> => {
  try {
    const { data } = await axiosInstance.get('/v1/sources/capability');

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const listSources = async (params: {
  page: number;
  limit: number;
  status?: string;
  search?: string;
}): Promise<ApiResponse<SourceOperationsPage<SourceSummary>>> => {
  try {
    const { data } = await axiosInstance.get('/v1/sources', { params });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getSourceOperations = async (
  sourceId: string,
  page = 1,
  limit = 10,
): Promise<ApiResponse<SourceOperationsDetail>> => {
  try {
    const { data } = await axiosInstance.get(
      `/v1/sources/${encodeURIComponent(sourceId)}/operations`,
      { params: { page, limit } },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const retrySource = async (
  sourceId: string,
  idempotencyKey: string,
): Promise<ApiResponse<SourceOperationAttempt>> => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/sources/${encodeURIComponent(sourceId)}/runs`,
      { kind: 'retry' },
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const reconnectSource = async (
  sourceId: string,
  idempotencyKey: string,
): Promise<ApiResponse<SourceReconnectAction>> => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/sources/${encodeURIComponent(sourceId)}/reconnect`,
      undefined,
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateSourceSettings = async (
  sourceId: string,
  automaticSyncEnabled: boolean,
  idempotencyKey: string,
  automaticSyncFrequency?: MEMORY_BATCH_RESYNC_FREQUENCY,
): Promise<ApiResponse<SourceSummary>> => {
  try {
    const patch = {
      automaticSyncEnabled,
      ...(automaticSyncFrequency === undefined
        ? {}
        : { automaticSyncFrequency }),
    };
    const { data } = await axiosInstance.patch(
      `/v1/sources/${encodeURIComponent(sourceId)}/settings`,
      patch,
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const listOperationalFailures = async (
  page: number,
  limit: number,
): Promise<ApiResponse<SourceOperationsPage<WorkspaceOperationalFailure>>> => {
  try {
    const { data } = await axiosInstance.get('/v1/admin/operational-failures', {
      params: { page, limit },
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const decideOperationalFailure = async (
  failureId: string,
  decision: 'ACKNOWLEDGE' | 'RETRY',
  idempotencyKey: string,
): Promise<ApiResponse<unknown>> => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/admin/operational-failures/${encodeURIComponent(failureId)}/decision`,
      { decision },
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const listSourceCatalog = async (params: {
  page: number;
  limit: number;
  status?: string;
  search?: string;
  role?: SourceRole;
  provider?: SourceProvider;
  types?: string[];
  folderKey?: string;
  sort?: SourceCatalogSort;
  externalResourceId?: string;
}): Promise<ApiResponse<SourceCatalogPage>> => {
  try {
    const { types, ...query } = params;
    const { data } = await axiosInstance.get('/v2/sources', {
      params: {
        ...query,
        ...(types?.length ? { types: types.join(',') } : {}),
      },
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export interface WebsitePageDiscovery {
  rootUrl: string;
  pages: string[];
}

export const discoverWebsitePages = async (
  rootUrl: string,
): Promise<ApiResponse<WebsitePageDiscovery>> => {
  try {
    const { data } = await axiosInstance.post('/v2/sources/website/discover', {
      rootUrl,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getSourceCatalogSource = async (
  sourceId: string,
): Promise<ApiResponse<SourceCatalogSummary>> => {
  try {
    const { data } = await axiosInstance.get(
      `/v2/sources/${encodeURIComponent(sourceId)}`,
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const listSourceFolders = async (): Promise<
  ApiResponse<{ data: SourceFolderSummary[] }>
> => {
  try {
    const { data } = await axiosInstance.get('/v2/sources/folders');

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const createSourceFolder = async (
  name: string,
  idempotencyKey: string,
): Promise<ApiResponse<SourceFolderSummary>> => {
  try {
    const { data } = await axiosInstance.post(
      '/v2/sources/folders',
      { name },
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const renameSourceFolder = async (
  folderId: string,
  name: string,
  idempotencyKey: string,
): Promise<ApiResponse<SourceFolderSummary>> => {
  try {
    const { data } = await axiosInstance.patch(
      `/v2/sources/folders/${encodeURIComponent(folderId)}`,
      { name },
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const deleteSourceFolder = async (
  folderId: string,
  idempotencyKey: string,
): Promise<ApiResponse<unknown>> => {
  try {
    const { data } = await axiosInstance.delete(
      `/v2/sources/folders/${encodeURIComponent(folderId)}`,
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateSourceCatalogMetadata = async (
  sourceId: string,
  patch: {
    roles?: SourceRole[];
    folderId?: string | null;
    displayName?: string;
    visibility?: SourceVisibility;
    useCaseInstructions?: string | null;
  },
  idempotencyKey: string,
): Promise<ApiResponse<SourceCatalogMetadata>> => {
  try {
    const { data } = await axiosInstance.patch(
      `/v2/sources/${encodeURIComponent(sourceId)}/catalog`,
      patch,
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateSourceSchedule = async (
  sourceId: string,
  patch: {
    actionMonitoringEnabled?: boolean;
    answerRefreshFrequency?: MEMORY_BATCH_RESYNC_FREQUENCY | null;
  },
  idempotencyKey: string,
): Promise<ApiResponse<SourceScheduleSummary>> => {
  try {
    const { data } = await axiosInstance.patch(
      `/v2/sources/${encodeURIComponent(sourceId)}/settings`,
      patch,
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const runSourceCatalog = async (
  sourceId: string,
  kind: 'sync' | 'retry',
  idempotencyKey: string,
): Promise<ApiResponse<SourceCatalogRunResult>> => {
  try {
    const { data } = await axiosInstance.post(
      `/v2/sources/${encodeURIComponent(sourceId)}/runs`,
      { kind },
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getSourceContent = async (
  sourceId: string,
  params: { page: number; limit: number; search?: string },
): Promise<ApiResponse<SourceContentPage>> => {
  try {
    const { data } = await axiosInstance.get(
      `/v2/sources/${encodeURIComponent(sourceId)}/content`,
      { params },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const retryFailedSourceContent = async (
  sourceId: string,
  idempotencyKey: string,
): Promise<ApiResponse<SourceContentRetryResult>> => {
  try {
    const { data } = await axiosInstance.post(
      `/v2/sources/${encodeURIComponent(sourceId)}/content/retry-failed`,
      undefined,
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const deleteSourceCatalog = async (
  sourceId: string,
  idempotencyKey: string,
): Promise<ApiResponse<unknown>> => {
  try {
    const { data } = await axiosInstance.delete(
      `/v2/sources/${encodeURIComponent(sourceId)}`,
      { headers: mutationHeaders(idempotencyKey) },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};
